import React, { useState, useEffect, useRef } from 'react';
import {
  FaFileAlt,
  FaChevronDown,
  FaKey,
  FaHandshake,
  FaHeadset,
  FaUser,
  FaBuilding
} from 'react-icons/fa';
import { FaRightFromBracket } from 'react-icons/fa6';
import { FaTriangleExclamation, FaCopy, FaCreditCard } from 'react-icons/fa6';
import { BR, US } from 'country-flag-icons/react/3x2';
import './header.css';
import useAuth from '../../hooks/useAuth';
import useInstances from '../../hooks/useInstances';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { apiRequest } from '../../config/api';
import { useTranslation } from 'react-i18next';

const languageOptions = {
  pt: { label: 'Português', flag: BR, country: 'Brasil' },
  en: { label: 'English', flag: US, country: 'United States' },
};

const Header = () => {
  const { signed, signout, user } = useAuth();
  const { activeConnections } = useInstances();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const [language, setLanguage] = useState('pt');
  const [showDropdown, setShowDropdown] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [userTotalInstances, setUserTotalInstances] = useState(null);
  const [userCreatedInstances, setUserCreatedInstances] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showPartnerModal, setShowPartnerModal] = useState(false);
  const [partnerPix, setPartnerPix] = useState('');
  const [partnerCoupon, setPartnerCoupon] = useState('');
  const [partnerPhone, setPartnerPhone] = useState('');
  const [partnerLoading, setPartnerLoading] = useState(false);
  const [partnerError, setPartnerError] = useState('');
  const [partnerSuccess, setPartnerSuccess] = useState('');
  const [partnerView, setPartnerView] = useState('loading');
  const [partnerUsers, setPartnerUsers] = useState([]);
  const [partnerCoupons, setPartnerCoupons] = useState([]);
  const [stripeLoading, setStripeLoading] = useState(false);
  const [stripeError, setStripeError] = useState('');
  const [isEnterprise, setIsEnterprise] = useState(false);
  const [enterpriseId, setEnterpriseId] = useState(null);
  const [debugInfo, setDebugInfo] = useState('');

  const dropdownRef = useRef();
  const profileRef = useRef();

  useEffect(() => {
    const handleAuthChange = () => {};
    
    window.addEventListener('auth-change', handleAuthChange);
    return () => window.removeEventListener('auth-change', handleAuthChange);
  }, []);

  useEffect(() => {
    const storedLang = localStorage.getItem('language');
    if (storedLang && languageOptions[storedLang]) {
      setLanguage(storedLang);
      i18n.changeLanguage(storedLang);
    }
  }, [i18n]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setShowProfile(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLanguageChange = (lang) => {
    setLanguage(lang);
    localStorage.setItem('language', lang);
    i18n.changeLanguage(lang);
    setShowDropdown(false);
  };

  const handleLogout = async () => {
    try {
      setIsLoggingOut(true);
      setShowLogoutModal(false);
      setShowProfile(false);
      await signout();
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
    } finally {
      setIsLoggingOut(false);
    }
  };

  const getUserName = () => {
    if (!user?.email) return 'Usuário';
    const emailPart = user.email.split('@')[0];
    return emailPart.charAt(0).toUpperCase() + emailPart.slice(1);
  };

  const CurrentFlag = languageOptions[language]?.flag;

  const getDocsLink = () => {
    return language === 'pt' 
      ? 'https://docs.wappfy.com.br/pt/introducao'
      : 'https://docs.wappfy.com.br/en/intro';
  };

  const getSupportLink = () => {
    return language === 'pt' 
      ? 'https://docs.wappfy.com.br/pt/contato'
      : 'https://docs.wappfy.com.br/en/contact';
  };

  useEffect(() => {
    const fetchUserData = async () => {
      if (!user?.id) return;

      try {
        // Buscar dados do usuário via backend
        const userData = await apiRequest(`/auth/user/${user.id}`);

        if (userData.success && userData.user) {
          setUserTotalInstances(userData.user.instances_total || 0);
          setIsEnterprise(userData.user.subscription_type === 'Enterprise');
          setEnterpriseId(userData.user.enterprise_id);
        }

        // Buscar contagem de instâncias via backend
        const instancesData = await apiRequest(`/instances/user/${user.id}`);
        if (instancesData.instances) {
          setUserCreatedInstances(instancesData.instances.length);
        }
      } catch (error) {
        console.error('Erro ao buscar dados do usuário:', error);
      }
    };
    fetchUserData();
  }, [user?.id]);

  const handleCopyUserId = () => {
    if (user?.id) {
      navigator.clipboard.writeText(user.id);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    }
  };

  const handlePartnerPhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 18);
    setPartnerPhone(value);
  };

  useEffect(() => {
    if (!showPartnerModal || !user?.id) return;
    setPartnerView('loading');
    setPartnerUsers([]);
    setPartnerCoupons([]);
    setPartnerPix('');
    setPartnerCoupon('');
    setPartnerPhone('');
    setPartnerError('');
    setPartnerSuccess('');
    setPartnerLoading(false);
    setDebugInfo('');

    const fetchPartnerData = async () => {
      try {
        // Buscar cupons via backend
        const couponsData = await apiRequest(`/coupons/user/${user.id}`);

        if (!couponsData.success || !couponsData.coupons || couponsData.coupons.length === 0) {
          setPartnerView('form');
          return;
        }

        setPartnerCoupons(couponsData.coupons);

        const couponCodes = couponsData.coupons.map(c => c.coupon_code).filter(Boolean);

        if (couponCodes.length === 0) {
          setPartnerView('form');
          return;
        }

        // Buscar usuários que usaram os cupons via backend
        const usersData = await apiRequest(`/users/by-coupons`, {
          method: 'POST',
          body: JSON.stringify({ couponCodes }),
        });

        if (!usersData.success) {
          console.error('Erro ao buscar usuários:', usersData);
          setPartnerView('form');
          return;
        }

        setPartnerUsers(usersData.users || []);
        setPartnerView('list');
        setDebugInfo(`Cupons: ${couponsData.coupons.length}, Usuários: ${usersData.users?.length || 0}`);
      } catch (error) {
        console.error('Erro geral no fetchPartnerData:', error);
        setPartnerView('form');
        setDebugInfo(`Erro: ${error.message}`);
      }
    };

    fetchPartnerData();
  }, [showPartnerModal, user?.id]);

  const handlePartnerSubmit = async (e) => {
    e.preventDefault();
    setPartnerError('');
    setPartnerSuccess('');
    setPartnerLoading(true);
    try {
      const body = {
        id: user?.id,
        pix: partnerPix,
        coupon_code: partnerCoupon,
        phone_number: partnerPhone,
      };
      const res = await fetch('https://automacao.wappfy.com.br/webhook/criarAfiliado', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (!res.ok) {
        setPartnerError('Erro ao enviar dados do parceiro.');
      } else {
        setPartnerSuccess('Cupom criado com sucesso!');
        setPartnerPix('');
        setPartnerCoupon('');
        setPartnerPhone('');
        setShowPartnerModal(false);
      }
    } catch {
      setPartnerError('Erro inesperado.');
    }
    setPartnerLoading(false);
  };

  const handleOpenBillingPortal = async () => {
    setStripeLoading(true);
    setStripeError('');
    try {
      // Buscar stripe_id via backend
      const userData = await apiRequest(`/users/${user.id}/subscription-status`);

      const idStripe = userData?.data?.stripe_id;
      if (!idStripe) {
        setStripeError('Assinatura não encontrada.');
        setStripeLoading(false);
        return;
      }

      const respData = await apiRequest('/stripe/billing-portal-session', {
        method: 'POST',
        body: JSON.stringify({
          customerId: idStripe,
        }),
      });

      if (respData.success && respData.url) {
        window.open(respData.url, '_blank');
      } else {
        setStripeError('Erro ao abrir portal de assinatura.');
      }
    } catch {
      setStripeError('Erro ao abrir portal de assinatura.');
    } finally {
      setStripeLoading(false);
    }
  };

  if (!signed) return null;

  if (isLoggingOut) {
    return (
      <div className="logout-loading-screen">
        <p>Saindo da conta...</p>
      </div>
    );
  }

  return (
    <>
      <header id="header" className="header">
        <div className="header-container">
          <div className="header-left">
            <div
              className="logo"
              style={{ cursor: 'pointer' }}
              onClick={() => navigate('/home')}
            >
              <span className="logo-text">Wappfy</span>
            </div>
          </div>

          <div className="header-right">
            {isEnterprise && enterpriseId && (
              <button
                className="docs-button"
                onClick={() => navigate(`/enterprise/${enterpriseId}`)}
              >
                <FaBuilding className="icon" />
                <span>Enterprise</span>
              </button>
            )}

            <div className="header-actions">
              <div className="connection-status">
                <div
                  className="status-indicator"
                  title={t('header.subscription')}
                  style={{ cursor: 'pointer' }}
                  onClick={handleOpenBillingPortal}
                >
                  <span className="status-text">
                    {userCreatedInstances !== null && userTotalInstances !== null
                      ? `${userCreatedInstances}/${userTotalInstances} ${t('header.instances')}`
                      : t('header.loading')
                    }
                  </span>
                </div>
              </div>

              <button
                className="docs-button"
                onClick={() => window.open(getDocsLink(), '_blank')}
                title={t('header.documentation')}
              >
                <FaFileAlt className="icon" />
                <span>{t('header.documentation')}</span>
              </button>
              <div className="language-selector" ref={dropdownRef}>
                <button
                  className="language-button"
                  onClick={() => setShowDropdown(!showDropdown)}
                  title={t('header.languageCurrent', { lang: languageOptions[language]?.label || language })}
                >
                  <div className="language-current">
                    {CurrentFlag && <CurrentFlag className="flag-icon" />}
                    <span className="language-code">{language.toUpperCase()}</span>
                  </div>
                  <FaChevronDown className={`chevron ${showDropdown ? 'open' : ''}`} />
                </button>

                {showDropdown && (
                  <div className="language-dropdown">
                    <div className="dropdown-header">
                      <span>{t('header.selectLanguage')}</span>
                    </div>
                    <div className="dropdown-content">
                      {Object.entries(languageOptions).map(([code, { label, flag: Flag, country }]) => (
                        <button
                          key={code}
                          onClick={() => handleLanguageChange(code)}
                          className={`dropdown-item ${code === language ? 'active' : ''}`}
                          title={country}
                        >
                          <Flag className="flag-icon-dropdown" />
                          <div className="language-info">
                            <span className="language-label">{label}</span>
                            <span className="language-country">{country}</span>
                          </div>
                          {code === language && <span className="check-mark">✓</span>}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div 
              className="profile-wrapper" 
              ref={profileRef}
            >
              <button
                className="profile-button"
                onClick={() => setShowProfile(!showProfile)}
                title={t('header.userMenu')}
              >
                <FaUser className="icon" />
                <span className="profile-name-only">{t('header.myProfile')}</span>
                <FaChevronDown className={`chevron ${showProfile ? 'open' : ''}`} />
              </button>

              {showProfile && (
                <div className="profile-dropdown">
                  <div className="profile-dropdown-header">
                    <div className="profile-details">
                      <h3 className="profile-username">{getUserName()}</h3>
                      <p className="profile-email-dropdown">{user?.email}</p>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        marginTop: 4,
                        marginBottom: 2
                      }}>
                        <span className="user-id" title={user?.id}>
                          {t('header.userId')}: {user?.id ? user.id.slice(0, 12) + '...' : ''}
                        </span>
                        <button
                          type="button"
                          onClick={handleCopyUserId}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: copied ? '#10b981' : '#fff',
                            cursor: 'pointer',
                            fontSize: 16,
                            padding: 2,
                            marginLeft: 2,
                            transition: 'color 0.2s'
                          }}
                          title={t('header.copyUserId')}
                        >
                          <FaCopy />
                        </button>
                        {copied && (
                          <span style={{ color: '#10b981', fontSize: 13, marginLeft: 4 }}>{t('header.copied')}</span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="profile-menu">
                    <button
                      className="profile-menu-item"
                      type="button"
                      onClick={() => {
                        setShowProfile(false);
                        navigate('/reset-password');
                      }}
                    >
                      <FaKey className="menu-icon" />
                      <div className="menu-content">
                        <span className="menu-title">{t('header.resetPassword')}</span>
                        <span className="menu-subtitle">{t('header.resetPasswordSubtitle')}</span>
                      </div>
                    </button>
                    
                    <button
                      className="profile-menu-item"
                      type="button"
                      onClick={() => {
                        setShowPartnerModal(true);
                        // Forçar recarregamento dos dados
                        setTimeout(() => {
                          setPartnerView('loading');
                        }, 100);
                      }}
                    >
                      <FaHandshake className="menu-icon" />
                      <div className="menu-content">
                        <span className="menu-title">{t('header.partner')}</span>
                        <span className="menu-subtitle">{t('header.partnerSubtitle')}</span>
                      </div>
                    </button>
                    
                    <button 
                      className="profile-menu-item" 
                      type="button"
                      onClick={() => window.open(getSupportLink(), '_blank')}
                    >
                      <FaHeadset className="menu-icon" />
                      <div className="menu-content">
                        <span className="menu-title">{t('header.support')}</span>
                        <span className="menu-subtitle">{t('header.needHelp')}</span>
                      </div>
                    </button>
                    
                    <button
                      className="profile-menu-item"
                      type="button"
                      onClick={handleOpenBillingPortal}
                      disabled={stripeLoading}
                    >
                      <FaCreditCard className="menu-icon" />
                      <div className="menu-content">
                        <span className="menu-title">{t('header.manageSubscription')}</span>
                        <span className="menu-subtitle">{t('header.billing')}</span>
                      </div>
                      {stripeLoading && (
                        <span style={{ marginLeft: 8, fontSize: 13, color: '#10b981' }}>{t('header.opening')}</span>
                      )}
                    </button>
                    {stripeError && (
                      <div style={{ color: '#ef4444', fontSize: 13, margin: '8px 0 0 16px' }}>
                        {stripeError}
                      </div>
                    )}
                    
                    <div className="menu-divider"></div>
                    
                    <button 
                      onClick={() => setShowLogoutModal(true)}
                      className="profile-menu-item logout-item"
                      type="button"
                    >
                      <FaRightFromBracket className="menu-icon" />
                      <div className="menu-content">
                        <span className="menu-title">{t('header.logout')}</span>
                        <span className="menu-subtitle">{t('header.endSession')}</span>
                      </div>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {showLogoutModal && (
        <div className="logout-modal-overlay" onClick={() => setShowLogoutModal(false)}>
          <div className="logout-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-content">
              <h2>{t('header.confirmLogout')}</h2>
              <p>{t('header.confirmLogoutMsg')}</p>
              <div className="modal-buttons">
                <button 
                  className="cancel-button"
                  onClick={() => setShowLogoutModal(false)}
                >
                  {t('header.cancel')}
                </button>
                <button 
                  className="confirm-button"
                  onClick={handleLogout}
                  disabled={isLoggingOut}
                >
                  {isLoggingOut ? t('header.loggingOut') : t('header.confirm')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Wappfy Partner */}
      {showPartnerModal && (
        <div className="partner-modal-overlay" onClick={() => setShowPartnerModal(false)}>
          <div className="partner-modal" onClick={e => e.stopPropagation()}>
            <div className="partner-modal-content">
              <h2>{t('header.partner', 'Wappfy Partner')}</h2>
              {partnerView === 'loading' && (
                <div style={{
                  textAlign: 'center',
                  padding: 24,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 12
                }}>
                  <div style={{
                    width: 32,
                    height: 32,
                    border: '3px solid rgba(62, 111, 92, 0.2)',
                    borderTop: '3px solid #3E6F5C',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></div>
                  <span style={{ color: '#3E6F5C', fontSize: 14, fontWeight: 600 }}>
                    {t('header.loading', 'Carregando dados...')}
                  </span>
                </div>
              )}
              {partnerView === 'form' && (
                <form onSubmit={handlePartnerSubmit} className="partner-form">
                  <label>
                    <span>{t('header.partnerPix', 'PIX')}:</span>
                    <input
                      type="text"
                      value={partnerPix}
                      onChange={e => setPartnerPix(e.target.value)}
                      required
                      placeholder={t('header.partnerPixPlaceholder', 'PIX key')}
                    />
                  </label>
                  <label>
                    <span>{t('header.partnerCoupon', 'Coupon code')}:</span>
                    <input
                      type="text"
                      value={partnerCoupon}
                      onChange={e => setPartnerCoupon(e.target.value)}
                      required
                      placeholder={t('header.partnerCouponPlaceholder', 'Coupon code')}
                    />
                  </label>
                  <label>
                    <span>{t('header.partnerPhone', 'Phone')}:</span>
                    <input
                      type="text"
                      value={partnerPhone}
                      onChange={handlePartnerPhoneChange}
                      required
                      placeholder={t('header.partnerPhonePlaceholder', 'Phone')}
                      maxLength={18}
                      inputMode="numeric"
                    />
                  </label>
                  {partnerError && <span style={{ color: '#ef4444', fontSize: 14, fontWeight: 500 }}>{t('header.partnerError', partnerError)}</span>}
                  {partnerSuccess && <span style={{ color: '#3E6F5C', fontSize: 14, fontWeight: 500 }}>{t('header.partnerSuccess', partnerSuccess)}</span>}
                  <div style={{ display: 'flex', gap: 12, marginTop: 16, justifyContent: 'space-between' }}>
                    <button
                      type="button"
                      className="partner-button-secondary"
                      onClick={() => {
                        setShowPartnerModal(false);
                        setDebugInfo('');
                      }}
                    >
                      {t('header.cancel')}
                    </button>
                    <button
                      type="submit"
                      className="partner-button-primary"
                      disabled={partnerLoading}
                    >
                      {partnerLoading ? t('header.sending', 'Sending...') : t('header.send', 'Send')}
                    </button>
                  </div>
                </form>
              )}
              {partnerView === 'list' && (
                <div>
                  {debugInfo && (
                    <div style={{ 
                      background: '#f0f9ff', 
                      border: '1px solid #0ea5e9', 
                      borderRadius: 8, 
                      padding: 8, 
                      marginBottom: 12,
                      fontSize: 12,
                      color: '#0369a1'
                    }}>
                      Debug: {debugInfo}
                    </div>
                  )}
                  <h3 style={{ marginBottom: 12, color: '#2D2D2D', fontWeight: 600 }}>{t('header.partnerYourCoupons', 'Your coupons')}</h3>
                  <ul style={{ marginBottom: 18, paddingLeft: 18 }}>
                    {partnerCoupons && partnerCoupons.length > 0 ? (
                      partnerCoupons.map(c => (
                        <li key={c.id} className="partner-coupon-item">
                          {c.coupon_code || 'Cupom sem código'}
                        </li>
                      ))
                    ) : (
                      <li style={{
                        color: '#6b7280',
                        fontStyle: 'italic',
                        padding: '8px 12px',
                        background: '#f9fafb',
                        borderRadius: 6,
                        border: '1px solid #D1D5DB'
                      }}>
                        Nenhum cupom encontrado
                      </li>
                    )}
                  </ul>

                  <h4 style={{ marginBottom: 8, color: '#2D2D2D', fontWeight: 600 }}>{t('header.partnerUsers', 'Users who used your coupons:')}</h4>
                  <div style={{ maxHeight: 220, overflowY: 'auto' }}>
                    {partnerUsers && partnerUsers.length > 0 ? (
                      partnerUsers.map((u, idx) => (
                        <div key={idx} className="partner-user-card">
                          <div><b>{t('header.partnerUserName', 'Name')}:</b> {u.name || 'Não informado'}</div>
                          <div><b>{t('header.partnerUserEmail', 'Email')}:</b> {u.email || 'Não informado'}</div>
                          <div><b>{t('header.partnerUserCoupon', 'Coupon used')}:</b> {u.coupon_code_used || 'Não informado'}</div>
                          {u.created_at && (
                            <div><b>Data de registro:</b> {new Date(u.created_at).toLocaleDateString('pt-BR')}</div>
                          )}
                        </div>
                      ))
                    ) : (
                      <div style={{ color: '#6b7280', fontSize: 15, textAlign: 'center', padding: 20 }}>
                        {t('header.partnerNoUsers', 'No users have used your coupons yet.')}
                      </div>
                    )}
                  </div>
                  
                  <div style={{ display: 'flex', gap: 8, marginTop: 18, flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      className="partner-button-secondary"
                      style={{ flex: 1, minWidth: '80px' }}
                      onClick={() => {
                        setDebugInfo('');
                        setPartnerView('loading');
                        // Forçar recarregamento dos dados
                        setTimeout(() => {
                          const fetchPartnerData = async () => {
                            try {
                              // Buscar cupons via backend
                              const couponsData = await apiRequest(`/coupons/user/${user.id}`);

                              if (!couponsData.success || !couponsData.coupons || couponsData.coupons.length === 0) {
                                setPartnerView('form');
                                return;
                              }

                              setPartnerCoupons(couponsData.coupons);

                              const couponCodes = couponsData.coupons.map(c => c.coupon_code).filter(Boolean);
                              if (couponCodes.length === 0) {
                                setPartnerView('form');
                                return;
                              }

                              // Buscar usuários via backend
                              const usersData = await apiRequest(`/users/by-coupons`, {
                                method: 'POST',
                                body: JSON.stringify({ couponCodes }),
                              });

                              if (!usersData.success) {
                                setPartnerView('form');
                                return;
                              }

                              setPartnerUsers(usersData.users || []);
                              setPartnerView('list');
                              setDebugInfo(`Cupons: ${couponsData.coupons.length}, Usuários: ${usersData.users?.length || 0}`);
                            } catch (error) {
                              console.error('Erro ao recarregar dados:', error);
                              setPartnerView('form');
                            }
                          };
                          fetchPartnerData();
                        }, 100);
                      }}
                    >
                      {t('header.refresh', 'Refresh')}
                    </button>
                    <button
                      type="button"
                      className="partner-button-primary"
                      style={{ flex: 1, minWidth: '80px' }}
                      onClick={() => setPartnerView('form')}
                    >
                      {t('header.addNew', 'Add New')}
                    </button>
                    <button
                      type="button"
                      className="partner-button-secondary"
                      style={{ flex: 1, minWidth: '80px' }}
                      onClick={() => {
                        setShowPartnerModal(false);
                        setDebugInfo('');
                      }}
                    >
                      {t('header.close', 'Close')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;